import { Button, Spin, Descriptions, Tag, Table, Collapse, Space, message } from 'antd';
import { HistoryOutlined, FileTextOutlined, PrinterOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrderDetail } from '../hooks/useOrderDetail';
import { useShipmentByOrderId } from '../hooks/useShipmentByOrderId';
import OrderTimeline from '../components/OrderTimeline';
import PdfPreviewModal from '../components/PdfPreviewModal';
import { printUniUniLabel, downloadPackingSlip } from '../api/uniuniShipments.api';

function buildTrackingUrl(
  carrierCode: string | undefined,
  trackingNumber: string | undefined
): string | null {
  if (!carrierCode || !trackingNumber) return null;

  const code = carrierCode.toLowerCase().trim();

  const trackingUrlMap: Record<string, (id: string) => string | null> = {
    stamps_com: (id) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${id}`,
    usps: (id) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${id}`,
    ups: (id) => `https://www.ups.com/track?tracknum=${id}`,
    ups_walleted: (id) => `https://www.ups.com/track?tracknum=${id}`,
    fedex: (id) => `https://www.fedex.com/fedextrack/?trknbr=${id}`,
    fedex_walleted: (id) => `https://www.fedex.com/fedextrack/?trknbr=${id}`,
    firstmile: (id) => `https://firstmile.com/track/${id}`,
    rr_donnelley: (id) => `https://www.epostglobalshipping.com/track?trackingnumber=${id}`,
    globalpost: (id) => `https://globalpost.com/track/${id}`,
    seko_ltl_walleted: (id) => `https://www.sekologistics.com/us/track-and-trace/?trackingNumber=${id}`,
    "voucher-generic": (_id) => null
  };

  const urlBuilder = trackingUrlMap[code];

  if (urlBuilder) {
    return urlBuilder(trackingNumber);
  }

  return `https://www.17track.net/en/track#nums=${trackingNumber}`;
}

export default function OrderDetail() {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const { data, isLoading } = useOrderDetail(orderId || '');
  const { data: shipmentData } = useShipmentByOrderId((data as any)?.orderNumber || '');

  const order: any = data;
  const shipmentResp: any = shipmentData;
  const shipment = shipmentResp?.shipments?.[0];

  const [previewModal, setPreviewModal] = useState<{
    open: boolean;
    title: string;
    filename: string;
    fetchBlob: () => Promise<Blob>;
  }>({ open: false, title: '', filename: '', fetchBlob: async () => new Blob() });

  const openSlipPreview = () => {
    if (!order?.orderId) return;
    const orderNumber = order.orderNumber ?? order.orderId;
    setPreviewModal({
      open: true,
      title: `Packing Slip — ${orderNumber}`,
      filename: `packing-slip-${orderNumber}.pdf`,
      fetchBlob: async () => (await downloadPackingSlip(order.orderId)).data as Blob,
    });
  };

  // UniUni-shipped orders don't have an SS shipment row (we use markasshipped
  // with carrierCode="other"), so `shipment.trackingNumber` is empty for them.
  // The UniUni tracking number is stamped on shipstation_orders at ship time,
  // so fall back to it — that's what the label endpoint actually needs.
  const uniuniTrackingNumber =
    shipment?.trackingNumber ?? order?.uniuni_tracking_number ?? null;

  const openLabelPreview = () => {
    const tno = uniuniTrackingNumber;
    if (!tno) {
      message.warning('No tracking number on this order yet — cannot preview label');
      return;
    }
    setPreviewModal({
      open: true,
      title: `Label — ${tno}`,
      filename: `label-${tno}.pdf`,
      fetchBlob: async () => (await printUniUniLabel(tno)).data as Blob,
    });
  };

  if (isLoading) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (!order) {
    return (
      <div style={{ padding: 20 }}>
        <h2>Order not found</h2>
        <Button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/orders'))}>← Back</Button>
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      {/* Uses browser history so it returns to wherever the user came from
          (Orders, UniUni Shipments, Shipping, etc.). Falls back to /orders
          only when there's no history entry (direct URL load). */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <Button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/orders'))}>
          ← Back
        </Button>
        <Space>
          <Button icon={<FileTextOutlined />} onClick={openSlipPreview}>
            Preview Packing Slip
          </Button>
          <Button
            icon={<PrinterOutlined />}
            type="primary"
            onClick={openLabelPreview}
            disabled={!uniuniTrackingNumber}
          >
            Preview Label
          </Button>
        </Space>
      </div>

      {/* Customer Name at Top Left */}
      <h1 style={{ marginBottom: 30, fontSize: 28, fontWeight: 600 }}>
        {order?.shipTo?.name || 'Unknown Customer'}
      </h1>

      {/* Order Information Section */}
      <h2 style={{ marginTop: 30, marginBottom: 15, fontSize: 20, borderBottom: '2px solid #f0f0f0', paddingBottom: 10 }}>
        Order Information
      </h2>
      <Descriptions bordered column={2} style={{ marginBottom: 30 }}>
        <Descriptions.Item label="Order Number" span={2}>{order.orderNumber}</Descriptions.Item>
        <Descriptions.Item label="Order Date">
          {order.orderDate ? new Date(order.orderDate).toLocaleDateString('en-US') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag>{order.orderStatus}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Ship Date">
          {shipment?.shipDate ? new Date(shipment.shipDate).toLocaleDateString('en-US') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Carrier Code">{shipment?.carrierCode || '-'}</Descriptions.Item>
        <Descriptions.Item label="Tracking Number" span={2}>
          {shipment?.trackingNumber
            ? buildTrackingUrl(shipment?.carrierCode, shipment?.trackingNumber)
              ? <a href={buildTrackingUrl(shipment?.carrierCode, shipment?.trackingNumber) || ''} target="_blank" rel="noopener noreferrer">{shipment?.trackingNumber}</a>
              : shipment?.trackingNumber
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Ship To Address" span={2}>
          <div>
            {order?.shipTo?.street1}
            {order?.shipTo?.street2 && <>, {order?.shipTo?.street2}</>}
            <br />
            {order?.shipTo?.city}, {order?.shipTo?.state} {order?.shipTo?.postalCode}
            <br />
            {order?.shipTo?.country}
          </div>
        </Descriptions.Item>
      </Descriptions>

      {/* Items Section */}
      <h2 style={{ marginTop: 30, marginBottom: 15, fontSize: 20, borderBottom: '2px solid #f0f0f0', paddingBottom: 10 }}>
        Items
      </h2>
      {order?.items && order.items.length > 0 ? (
        <Descriptions bordered column={1} style={{ marginBottom: 30 }}>
          {order.items.map((item: any, idx: number) => (
            <Descriptions.Item label={`Item ${idx + 1}`} key={idx}>
              {item.name} (SKU: {item.sku || '-'}) - Qty: {item.quantity}
            </Descriptions.Item>
          ))}
        </Descriptions>
      ) : (
        <p style={{ marginBottom: 30 }}>No items found</p>
      )}

      {/* Shipping & Tracking Details Section */}
      <h2 style={{ marginTop: 30, marginBottom: 15, fontSize: 20, borderBottom: '2px solid #f0f0f0', paddingBottom: 10 }}>
        Shipping & Tracking Details
      </h2>
      {shipment ? (
        <Descriptions bordered column={2} style={{ marginBottom: 30 }}>
          <Descriptions.Item label="Ship Date">
            {shipment.shipDate ? new Date(shipment.shipDate).toLocaleDateString('en-US') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Carrier Code">{shipment.carrierCode || '-'}</Descriptions.Item>
          <Descriptions.Item label="Tracking Number" span={2}>
            {shipment.trackingNumber
              ? buildTrackingUrl(shipment.carrierCode, shipment.trackingNumber)
                ? <a href={buildTrackingUrl(shipment.carrierCode, shipment.trackingNumber) || ''} target="_blank" rel="noopener noreferrer">{shipment.trackingNumber}</a>
                : shipment.trackingNumber
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Customer Name" span={2}>{shipment?.shipTo?.name || '-'}</Descriptions.Item>
          <Descriptions.Item label="City">{shipment?.shipTo?.city || '-'}</Descriptions.Item>
          <Descriptions.Item label="State">{shipment?.shipTo?.state || '-'}</Descriptions.Item>
          <Descriptions.Item label="Postal Code">{shipment?.shipTo?.postalCode || '-'}</Descriptions.Item>
          <Descriptions.Item label="Return Label">{shipment.returnLabel ? 'Yes' : 'No'}</Descriptions.Item>
          <Descriptions.Item label="Voided" span={2}>{shipment.voided ? 'Yes' : 'No'}</Descriptions.Item>
        </Descriptions>
      ) : (
        <p>No shipment information available</p>
      )}

      <PdfPreviewModal
        open={previewModal.open}
        onClose={() => setPreviewModal((p) => ({ ...p, open: false }))}
        title={previewModal.title}
        filename={previewModal.filename}
        fetchBlob={previewModal.fetchBlob}
      />

      <Collapse
        defaultActiveKey={['activity']}
        style={{ marginTop: 30 }}
        items={[
          {
            key: 'activity',
            label: (
              <span style={{ fontSize: 16, fontWeight: 600 }}>
                <HistoryOutlined style={{ marginRight: 8 }} />
                Order Activity
              </span>
            ),
            children: <OrderTimeline orderId={order.orderId} />,
          },
        ]}
      />
    </div>
  );
}
